import { Link } from "react-router-dom";
import { AuthBrandPill } from "@/layouts/AuthLayout.jsx";
import { cx } from "@/lib/cx.js";

/*
 * The card both auth pages sit in, and the banner they announce results with.
 *
 * Ported from the shared block in login.html / register.html. Callout is the
 * wrong component for the banner: it is a quiet inline hint for a form section,
 * whereas this is the page telling you whether you got in.
 */

export function AuthCard({ eyebrow, title, subtitle, width = "max-w-[420px]", children, footer }) {
    return (
        <div className={cx("w-full animate-fade-up", width)}>
            <AuthBrandPill />

            <div className="rounded-2xl border border-slate-200/70 bg-white p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-12px_rgba(15,23,42,0.10)] sm:p-9 dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-7">
                    <span className="inline-block rounded-full bg-gradient-to-r from-brand-500/10 to-violet-500/10 px-2.5 py-1 text-[11px] font-semibold tracking-wider text-brand-700 uppercase dark:text-brand-300">
                        {eyebrow}
                    </span>
                    <h1 className="mt-4 text-[26px] leading-tight font-bold tracking-tight">{title}</h1>
                    {subtitle && (
                        <p className="mt-1.5 text-[14.5px] text-slate-500 dark:text-slate-400">
                            {subtitle}
                        </p>
                    )}
                </div>

                {children}

                {footer && (
                    <div className="mt-7 border-t border-slate-100 pt-5 text-center dark:border-slate-800">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

const BANNER = {
    error: {
        box: "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40",
        text: "text-rose-700 dark:text-rose-300",
        icon: "text-rose-500",
    },
    success: {
        box: "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40",
        text: "text-emerald-700 dark:text-emerald-300",
        icon: "text-emerald-500",
    },
};

/**
 * Result banner above a form.
 *
 * role="alert" so a screen reader announces it: someone who cannot see the
 * banner appear would otherwise get no feedback at all that the sign-in failed.
 */
export function AuthBanner({ tone = "error", children }) {
    if (!children) return null;
    const t = BANNER[tone] ?? BANNER.error;

    return (
        <div
            role="alert"
            data-auth-banner={tone}
            className={cx("mb-5 flex items-start gap-2.5 rounded-lg border px-3.5 py-3", t.box)}
        >
            <span className={cx("mt-px shrink-0", t.icon)}>
                {tone === "success" ? (
                    <svg
                        className="size-4.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                ) : (
                    <svg
                        className="size-4.5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v4.5M12 16h.01" />
                    </svg>
                )}
            </span>
            <p className={cx("text-[13px] font-medium", t.text)}>{children}</p>
        </div>
    );
}

/** The full-width gradient submit button both pages use. */
export function AuthSubmit({ loading, loadingLabel, children, ...rest }) {
    return (
        <button
            type="submit"
            disabled={loading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-brand-600 bg-gradient-to-r from-brand-500 to-violet-500 text-[15px] font-semibold tracking-wide text-white shadow-[0_8px_20px_-8px_rgba(99,102,241,0.55)] transition hover:-translate-y-px hover:shadow-[0_12px_26px_-8px_rgba(99,102,241,0.7)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70"
            {...rest}
        >
            {loading && (
                <svg
                    className="size-4.5 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    aria-hidden="true"
                >
                    <path d="M21 12a9 9 0 1 1-6.22-8.56" />
                </svg>
            )}
            {loading ? loadingLabel || children : children}
        </button>
    );
}

/** "Register here" / "Sign in instead" — same styling on both pages. */
export function AuthLink({ to, children }) {
    return (
        <Link
            to={to}
            className="font-semibold text-brand-600 hover:underline dark:text-brand-400"
        >
            {children}
        </Link>
    );
}
